
import React, { useState, useEffect, useMemo } from 'react';
import { MeetingNote, AppState, SyncSources } from './types';
import { summarizeMeetings } from './services/geminiService';
import { fetchGmailMeetingNotes } from './services/gmailService';
import { fetchDriveItems } from './services/driveService';
import { fetchCalendarEvents } from './services/calendarService';
import { fetchChatMessages } from './services/chatService';
import Header from './components/Header';
import NoteUpload from './components/NoteUpload';
import MeetingList from './components/MeetingList';
import Dashboard from './components/Dashboard';
import GoogleSync from './components/GoogleSync';
import FloatingChat from './components/FloatingChat';
import CalendarDrawer from './components/CalendarDrawer';

const CLIENT_ID = "429058181700-1b8dtm55m90sbk3s4aak7jj474katfd6.apps.googleusercontent.com";
const STORAGE_KEY = 'meetingbot_access_token';
const SOURCES_STORAGE_KEY = 'meetingbot_selected_sources';

const notifyUser = (title: string, body: string) => {
  if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
    new Notification(title, { body, icon: '/logo.png' });
  }
};

const App: React.FC = () => {
  const [state, setState] = useState<AppState>(() => {
    const savedToken = localStorage.getItem(STORAGE_KEY);
    let initialSources: SyncSources = { gmail: true, drive: true, calendar: true, chat: false };
    try {
      const savedSources = localStorage.getItem(SOURCES_STORAGE_KEY);
      if (savedSources) {
        initialSources = JSON.parse(savedSources);
      }
    } catch (e) {
      console.warn("Failed to parse saved sources", e);
    }

    return {
      notes: [],
      calendarEvents: [],
      summary: null,
      isLoading: false,
      isSyncing: false,
      syncProgress: null,
      isGoogleConnected: !!savedToken,
      accessToken: savedToken,
      userEmail: null,
      userName: null,
      userSub: null,
      userAvatar: null,
      error: null,
      selectedSources: initialSources,
    };
  });

  const [syncDays, setSyncDays] = useState(7);
  const [tokenClient, setTokenClient] = useState<any>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const matchMedia = window.matchMedia('(display-mode: standalone)');
      setIsStandalone(matchMedia.matches);
      matchMedia.addEventListener('change', (e) => {
        setIsStandalone(e.matches);
      });
    }
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const win = window as any;
    if (win.deferredInstallPrompt) {
      setDeferredPrompt(win.deferredInstallPrompt);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    const handleInstallPromptReady = () => {
      if (win.deferredInstallPrompt) {
        setDeferredPrompt(win.deferredInstallPrompt);
      }
    };
    window.addEventListener('install-prompt-ready', handleInstallPromptReady);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('install-prompt-ready', handleInstallPromptReady);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    } else {
      setShowInstallModal(true);
    }
  };

  // Derived state: Only show notes that match selected sources OR were added manually
  const activeNotes = useMemo(() => {
    return state.notes.filter(note => {
      if (note.source === 'manual') return true;
      if (note.source === 'gmail' && state.selectedSources.gmail) return true;
      if ((note.source === 'gdoc' || note.source === 'gsheet') && state.selectedSources.drive) return true;
      if (note.source === 'chat' && state.selectedSources.chat) return true;
      return false;
    });
  }, [state.notes, state.selectedSources]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch('/api/me');
        if (response.ok) {
          const data = await response.json();
          if (data.email) {
            setState(prev => ({ 
              ...prev, 
              userEmail: data.email,
              userName: data.name || prev.userName
            }));
          }
        }
      } catch (err) {
        console.warn("Auth check error");
      }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    const fetchUserInfo = async (token: string) => {
      try {
        const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setState(prev => ({ 
            ...prev, 
            userAvatar: data.picture, 
            userEmail: data.email || prev.userEmail,
            userName: data.name || prev.userName,
            userSub: data.sub || prev.userSub
          }));
        }
      } catch (e) {
        console.error("Failed to fetch user info", e);
      }
    };

    if (state.accessToken) {
      fetchUserInfo(state.accessToken);
    }
  }, [state.accessToken]);

  useEffect(() => {
    const initGsi = () => {
      if (typeof window !== 'undefined' && (window as any).google?.accounts?.oauth2) {
        try {
          const client = (window as any).google.accounts.oauth2.initTokenClient({
            client_id: CLIENT_ID,
            scope: 'https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/spreadsheets.readonly https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/chat.spaces.readonly https://www.googleapis.com/auth/chat.messages.readonly https://www.googleapis.com/auth/chat.messages.create https://www.googleapis.com/auth/chat.spaces.create https://www.googleapis.com/auth/chat.memberships.readonly https://www.googleapis.com/auth/profile.emails.read https://www.googleapis.com/auth/directory.readonly',
            callback: (response: any) => {
              if (response.access_token) {
                localStorage.setItem(STORAGE_KEY, response.access_token);
                setState(prev => ({ 
                  ...prev, 
                  accessToken: response.access_token, 
                  isGoogleConnected: true,
                  error: null
                }));
              }
            },
          });
          setTokenClient(client);
        } catch (e) {
          console.error("GIS Init Error", e);
        }
      } else {
        setTimeout(initGsi, 500);
      }
    };
    initGsi();
  }, []);

  const handleConnectGoogle = (prompt: string = 'select_account') => {
    if (tokenClient) {
      tokenClient.requestAccessToken({ prompt });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setState(prev => ({
      ...prev,
      isGoogleConnected: false,
      accessToken: null,
      userEmail: null,
      userName: null,
      userSub: null,
      userAvatar: null,
      notes: [],
      calendarEvents: [],
      summary: null,
      error: null
    }));
  };

  // Keep session alive by refreshing token every 50 minutes
  useEffect(() => {
    if (state.accessToken && tokenClient) {
      const interval = setInterval(() => {
        handleConnectGoogle('');
      }, 50 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [state.accessToken, tokenClient]);

  const setSyncSources = (sources: SyncSources) => {
    localStorage.setItem(SOURCES_STORAGE_KEY, JSON.stringify(sources));
    setState(prev => ({ ...prev, selectedSources: sources, summary: null }));
  };

  const handleSyncWorkspace = async () => {
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    if (!state.accessToken) {
      handleConnectGoogle();
      return;
    }

    if (!state.selectedSources.gmail && !state.selectedSources.drive && !state.selectedSources.calendar && !state.selectedSources.chat) {
      setState(prev => ({ ...prev, error: "Please select at least one data source to sync." }));
      return;
    }
    
    setState(prev => ({ 
      ...prev, 
      isSyncing: true, 
      syncProgress: 'Initializing sync...', 
      error: null,
      syncDetails: {
        ...(state.selectedSources.gmail ? { Gmail: 'pending' } : {}),
        ...(state.selectedSources.drive ? { Drive: 'pending' } : {}),
        ...(state.selectedSources.calendar ? { Calendar: 'pending' } : {}),
        ...(state.selectedSources.chat ? { Chat: 'pending' } : {}),
      }
    }));
    
    try {
      const activeTasks = new Set<string>();
      const updateProgress = () => {
        if (activeTasks.size === 0) {
          setState(prev => ({ ...prev, syncProgress: 'Processing results...' }));
        } else {
          const tasks = Array.from(activeTasks).join(', ');
          setState(prev => ({ ...prev, syncProgress: `Fetching ${tasks}...` }));
        }
      };

      const wrapTask = async (name: string, taskPromise: Promise<any>) => {
        activeTasks.add(name);
        setState(prev => ({
          ...prev,
          syncDetails: { ...prev.syncDetails, [name]: 'syncing' }
        }));
        updateProgress();
        try {
          const result = await taskPromise;
          setState(prev => ({
            ...prev,
            syncDetails: { ...prev.syncDetails, [name]: 'done' }
          }));
          return result;
        } catch (err) {
          setState(prev => ({
            ...prev,
            syncDetails: { ...prev.syncDetails, [name]: 'error' }
          }));
          throw err;
        } finally {
          activeTasks.delete(name);
          updateProgress();
        }
      };

      const fetchTasks: { type: 'gmail' | 'drive' | 'calendar' | 'chat'; task: Promise<any[]> }[] = [];
      if (state.selectedSources.gmail) {
        fetchTasks.push({ type: 'gmail', task: wrapTask('Gmail', fetchGmailMeetingNotes(state.accessToken, syncDays)) });
      }
      if (state.selectedSources.drive) {
        fetchTasks.push({ type: 'drive', task: wrapTask('Drive', fetchDriveItems(state.accessToken, syncDays)) });
      }
      if (state.selectedSources.calendar) {
        fetchTasks.push({ type: 'calendar', task: wrapTask('Calendar', fetchCalendarEvents(state.accessToken, syncDays)) });
      }
      if (state.selectedSources.chat) {
        fetchTasks.push({ type: 'chat', task: wrapTask('Chat', fetchChatMessages(state.accessToken, syncDays, state.userName || '', state.userSub || '')) });
      }

      const results = await Promise.all(fetchTasks.map(t => t.task));
      
      let allNewNotes: MeetingNote[] = [];
      let newCalendarEvents: any[] = [];

      results.forEach((result, index) => {
        if (fetchTasks[index].type === 'calendar') {
          newCalendarEvents = result;
        } else {
          allNewNotes = [...allNewNotes, ...result];
        }
      });
      
      setState(prev => {
        const newlySyncedIds = new Set(allNewNotes.map(n => n.id));
        const activeSourceTypes = fetchTasks.map(t => t.type);

        const remainingNotes = prev.notes.filter(note => {
          const isFromActiveSource = 
            (note.source === 'gmail' && activeSourceTypes.includes('gmail')) ||
            ((note.source === 'gdoc' || note.source === 'gsheet') && activeSourceTypes.includes('drive')) ||
            (note.source === 'chat' && activeSourceTypes.includes('chat'));
          
          if (isFromActiveSource) return newlySyncedIds.has(note.id);
          return true;
        });

        const finalNotes = [...allNewNotes];
        const finalIds = new Set(allNewNotes.map(n => n.id));

        remainingNotes.forEach(note => {
          if (!finalIds.has(note.id)) finalNotes.push(note);
        });

        return {
          ...prev,
          notes: finalNotes,
          calendarEvents: state.selectedSources.calendar ? newCalendarEvents : prev.calendarEvents,
          isSyncing: false,
          syncProgress: null,
          summary: null,
          error: (allNewNotes.length === 0 && newCalendarEvents.length === 0) ? `No workspace updates found in the last ${syncDays} days.` : null
        };
      });

      if (allNewNotes.length > 0 || newCalendarEvents.length > 0) {
        notifyUser("Workspace Synced", `Successfully synced ${allNewNotes.length} items and ${newCalendarEvents.length} events.`);
      }
    } catch (err: any) {
      const errorMsg = err.message || "";
      const isAuthError = errorMsg.includes('401') || errorMsg.includes('403') || errorMsg.includes('Unauthorized');

      if (isAuthError) {
        localStorage.removeItem(STORAGE_KEY);
        setState(prev => ({ 
          ...prev, 
          isSyncing: false, 
          syncProgress: null,
          isGoogleConnected: false, 
          accessToken: null,
          error: "Authentication expired. Please re-authorize." 
        }));
      } else {
        setState(prev => ({ ...prev, isSyncing: false, syncProgress: null, error: errorMsg }));
      }
    }
  };

  const generateSummary = async () => {
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    if (activeNotes.length === 0) {
      setState(prev => ({ ...prev, error: "No workspace items available." }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      console.log("Generating summary for notes:", activeNotes);
      const summary = await summarizeMeetings(activeNotes);
      setState(prev => ({ ...prev, summary, isLoading: false }));
      notifyUser("Intelligence Summary Ready", "Your workspace summary has been successfully generated.");
    } catch (err: any) {
      console.error("Summary Generation Error:", err);
      setState(prev => ({ ...prev, isLoading: false, error: err.message }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-tr from-rose-50 via-indigo-50/50 to-indigo-100/30 relative text-slate-900">
      <Header 
        userEmail={state.userEmail} 
        userAvatar={state.userAvatar}
        onLogout={handleLogout}
        showInstallButton={!isStandalone} 
        onInstallClick={handleInstallClick}
        isGoogleConnected={state.isGoogleConnected}
      />
      
      {/* Floating Agenda Button */}
      {state.isGoogleConnected && (
        <button
          onClick={() => setIsCalendarOpen(true)}
          className="fixed top-20 right-0 bg-indigo-600 text-white px-4 py-3 rounded-l-2xl shadow-lg hover:bg-indigo-700 transition-all flex items-center space-x-2 z-30 group"
        >
          <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="font-bold text-sm">Agenda</span>
          {state.calendarEvents.length > 0 && (
            <span className="bg-white text-indigo-600 text-xs font-bold px-2 py-0.5 rounded-full ml-1">
              {state.calendarEvents.length}
            </span>
          )}
        </button>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {state.error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center justify-between animate-in fade-in slide-in-from-top-2">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium">{state.error}</span>
            </div>
            <button onClick={() => setState(p => ({ ...p, error: null }))} className="text-red-500 hover:text-red-700">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4 space-y-6">
            <GoogleSync 
              isConnected={state.isGoogleConnected}
              isSyncing={state.isSyncing}
              onConnect={() => handleConnectGoogle('select_account')}
              onSync={handleSyncWorkspace}
              syncDays={syncDays}
              setSyncDays={setSyncDays}
              selectedSources={state.selectedSources}
              onSourcesChange={setSyncSources}
            />
            <NoteUpload onNoteAdded={(n) => setState(p => ({...p, notes: [n, ...p.notes], summary: null}))} />
            <MeetingList 
              notes={activeNotes} 
              onRemove={(id) => setState(p => ({...p, notes: p.notes.filter(n => n.id !== id), summary: null}))} 
              onSummarize={generateSummary}
              isProcessing={state.isLoading}
            />
          </div>
          <div className="lg:col-span-8">
            {state.isLoading ? (
              <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center flex flex-col items-center justify-center min-h-[500px] space-y-8">
                 <div className="relative">
                   <div className="w-20 h-20 border-4 border-emerald-100 rounded-full"></div>
                   <div className="w-20 h-20 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
                   <div className="absolute inset-0 flex items-center justify-center">
                     <span className="text-xl">🧠</span>
                   </div>
                 </div>
                 <div className="space-y-3 w-full max-w-md">
                   <h2 className="text-2xl font-bold text-slate-900">Synthesizing Intelligence</h2>
                   <p className="text-slate-500 font-medium animate-pulse">Analyzing {activeNotes.length} workspace items...</p>
                 </div>
              </div>
            ) : state.isSyncing ? (
              <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center flex flex-col items-center justify-center min-h-[500px] space-y-8">
                 <div className="relative">
                   <div className="w-20 h-20 border-4 border-indigo-100 rounded-full"></div>
                   <div className="w-20 h-20 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
                   <div className="absolute inset-0 flex items-center justify-center">
                     <span className="text-xl">⚡</span>
                   </div>
                 </div>
                 <div className="space-y-3 w-full max-w-md">
                   <h2 className="text-2xl font-bold text-slate-900">Syncing Workspace</h2>
                   {state.syncProgress && (
                     <p className="text-slate-500 font-medium animate-pulse">{state.syncProgress}</p>
                   )}
                   
                   {state.syncDetails && (
                     <div className="mt-8 bg-slate-50 rounded-2xl p-6 border border-slate-100 space-y-4 text-left shadow-inner">
                       {Object.entries(state.syncDetails).map(([source, status]) => (
                         <div key={source} className="flex items-center justify-between">
                           <div className="flex items-center space-x-3">
                             <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                               status === 'done' ? 'bg-emerald-100 text-emerald-600' :
                               status === 'syncing' ? 'bg-indigo-100 text-indigo-600' :
                               status === 'error' ? 'bg-red-100 text-red-600' :
                               'bg-slate-200 text-slate-400'
                             }`}>
                               {source === 'Gmail' && '📧'}
                               {source === 'Drive' && '📁'}
                               {source === 'Calendar' && '📅'}
                               {source === 'Chat' && '💬'}
                             </div>
                             <span className={`font-bold ${status === 'pending' ? 'text-slate-400' : 'text-slate-700'}`}>
                               {source}
                             </span>
                           </div>
                           <div>
                             {status === 'done' && (
                               <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                             )}
                             {status === 'syncing' && (
                               <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                             )}
                             {status === 'pending' && (
                               <div className="w-5 h-5 border-2 border-slate-300 rounded-full"></div>
                             )}
                             {status === 'error' && (
                               <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" /></svg>
                             )}
                           </div>
                         </div>
                       ))}
                     </div>
                   )}
                 </div>
              </div>
            ) : state.summary ? (
              <Dashboard 
                summary={state.summary} 
                notes={activeNotes} 
                accessToken={state.accessToken}
                userEmail={state.userEmail}
              />
            ) : (
              <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center flex flex-col items-center justify-center min-h-[500px]">
                <h2 className="text-3xl font-extrabold text-slate-900 mb-4">Unified Intelligence</h2>
                <p className="text-slate-500 max-w-sm mx-auto mb-10">Sync your workspace to generate high-level summaries and perform interactive research.</p>
                {state.isGoogleConnected ? (
                  <p className="text-slate-500 font-medium bg-slate-50 px-6 py-4 rounded-xl border border-slate-100">
                    Select items from your workspace on the left and click "Summarize Workspace" to begin.
                  </p>
                ) : (
                  <button 
                    onClick={() => handleConnectGoogle('select_account')}
                    className="bg-indigo-600 text-white px-10 py-5 rounded-2xl font-bold shadow-xl hover:scale-105 transition-all"
                  >
                    Link Google Account
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Floating Intelligence Chat Component */}
      {state.summary && (
        <FloatingChat summary={state.summary} notes={activeNotes} />
      )}

      {/* Calendar Drawer */}
      <CalendarDrawer 
        isOpen={isCalendarOpen} 
        onClose={() => setIsCalendarOpen(false)} 
        events={state.calendarEvents}
        notes={activeNotes}
        accessToken={state.accessToken}
        onAddNote={(n) => setState(p => ({...p, notes: [n, ...p.notes], summary: null}))}
        onUpdateNote={(id, updates) => setState(p => ({
          ...p, 
          notes: p.notes.map(note => note.id === id ? { ...note, ...updates } : note),
          summary: null
        }))}
      />
      {showInstallModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-indigo-50/50">
              <h2 className="text-xl font-bold text-slate-800 flex items-center">
                <svg className="w-5 h-5 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Install MeetingBot
              </h2>
              <button onClick={() => setShowInstallModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4 text-slate-600 text-sm">
              <p>Due to browser security settings inside standard preview windows, automatic installation is not available.</p>
              
              <h3 className="font-bold text-slate-800 mt-4 mb-2">How to install via your browser:</h3>
              <ol className="list-decimal pl-5 space-y-2.5 marker:font-bold marker:text-indigo-500">
                <li>If you are in a preview window, open the app in a <strong>New Tab</strong> first using the arrow icon in the top right.</li>
                <li>Look at the far right of your browser's address bar.</li>
                <li>Click the <strong>Install</strong> icon (looks like a screen with a down arrow).</li>
                <li>Alternatively, click your browser's 3-dot menu and select <strong>Install App</strong> or <strong>Add to Home Screen</strong>.</li>
              </ol>
            </div>
            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end">
              <button 
                onClick={() => setShowInstallModal(false)}
                className="px-6 py-2 bg-indigo-600 font-bold text-white rounded-xl hover:bg-indigo-700 transition shadow-sm"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
